import { Controller } from '@hotwired/stimulus'
import consumer from '../channels/consumer'

// Subscribes an element to a conversation's cable stream and turns broadcast
// envelopes into DOM events that other controllers (or plain JS) can render from.
// This is the transport piece — it owns the reliability contract (reconnect +
// after_id catch-up + dedupe) and nothing about presentation.
//
// Usage:
//   <div data-controller="conversation"
//        data-conversation-conversation-id-value="<uuid>"
//        data-conversation-messages-url-value="/api/conversations/<uuid>/messages">
//
// Events dispatched on the element (detail = entity JSON):
//   conversation:message      — a new message (live or catch-up), deduped by id
//   conversation:read         — a membership's read pointer advanced
//   conversation:connected    — socket (re)established; catch-up runs after this
//   conversation:disconnected — socket dropped; the consumer retries on its own
export default class extends Controller {
  static values = {
    conversationId: String,
    messagesUrl: String, // optional: enables after_id catch-up on (re)connect
  }

  connect() {
    this.seenIds = new Set()
    this.lastId = null

    this.subscription = consumer.subscriptions.create(
      { channel: 'ConversationChannel', conversation_id: this.conversationIdValue },
      {
        connected: () => this.cableConnected(),
        disconnected: () => this.dispatch('disconnected'),
        received: envelope => this.received(envelope),
      },
    )
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
  }

  // Sockets die on every deploy and network blip. The consumer reconnects with
  // backoff on its own; our job on each (re)connect is to fill the gap — fetch
  // anything missed since the last message we saw.
  async cableConnected() {
    this.dispatch('connected')
    await this.catchUp()
  }

  async catchUp() {
    if (!this.messagesUrlValue) return

    const url = new URL(this.messagesUrlValue, window.location.origin)
    if (this.lastId) url.searchParams.set('after_id', this.lastId)

    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
    if (!response.ok) return

    const messages = await response.json()
    messages.forEach(message => this.handleMessage(message))
  }

  received(envelope) {
    if (envelope.v !== 1) return

    switch (envelope.event) {
    case 'message.created':
      this.handleMessage(envelope.data)
      break
    case 'read.updated':
      this.dispatch('read', { detail: envelope.data })
      break
    default:
      // future event types ride the same stream; ignore unknown ones
      break
    }
  }

  // Single funnel for live + catch-up messages: dedupe by id (a live message can
  // also arrive in the catch-up page) and track the cursor. UUIDv7 ids compare
  // chronologically as strings, which is what makes lastId a valid cursor.
  handleMessage(message) {
    if (this.seenIds.has(message.id)) return

    this.seenIds.add(message.id)
    if (!this.lastId || message.id > this.lastId) this.lastId = message.id
    this.dispatch('message', { detail: message })
  }
}
